import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link2, Search, Store, Unlink } from "lucide-react";
import { toast } from "sonner";
import type { MarketplaceListingOptionDTO } from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { formatApiError } from "@/lib/format-api-error";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMarketplaceListings } from "@/features/admin/listing-changes/use-marketplace-listings";

// Partner-detail "Marketplace listing" card (0091). Links a partner to one
// row of the integrations-marketplace project so the portal "My Listing"
// self-service surface activates for them. One listing per partner —
// enforced server-side by idx_partners_marketplace_integration, surfaced
// here as the 409 listing_already_linked toast.
//
// The picker mirrors integrations-tab.tsx's LinkPickerDialog, but the
// catalogue is tiny (~8 rows) so search filters client-side over name/slug.

// PATCH /api/partners/:id — body { marketplace_integration_id: number|null }.
// null unlinks. Invalidates both partner-detail namespaces (the Phase 6
// `partners.detail` read this page renders from, plus the admin list
// aggregate namespace) so the card and any admin partner grids refresh.
export function useLinkMarketplaceListing(partnerId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, number | null>({
    mutationFn: (marketplaceIntegrationId) =>
      apiFetch<{ ok: true }>(`/partners/${partnerId}`, {
        method: "PATCH",
        body: JSON.stringify({
          marketplace_integration_id: marketplaceIntegrationId,
        }),
      }),
    onSuccess: (_data, marketplaceIntegrationId) => {
      toast.success(
        marketplaceIntegrationId === null ? "Listing unlinked" : "Listing linked",
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.partners.detail(partnerId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.admin.partners.all,
      });
    },
    onError: (err) => {
      if (err.status === 409) {
        toast.error("That listing is already linked to another partner");
        return;
      }
      toast.error(formatApiError(err, "Failed to update the listing link"));
    },
  });
}

export function MarketplaceListingCard({
  partnerId,
  marketplaceIntegrationId,
}: {
  partnerId: string;
  marketplaceIntegrationId: number | null;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const linked = marketplaceIntegrationId !== null;
  // Fetch when we need to resolve the linked id to a name, or while the
  // picker is open. staleTime 60s on the hook keeps this cheap.
  const listingsQuery = useMarketplaceListings(linked || pickerOpen);
  const link = useLinkMarketplaceListing(partnerId);

  const listing = linked
    ? (listingsQuery.data?.listings ?? []).find(
        (l) => l.id === marketplaceIntegrationId,
      ) ?? null
    : null;

  return (
    <section className="rounded-xl border bg-card">
      <header className="flex items-center justify-between border-b border-border/60 px-5 py-3">
        <h3 className="inline-flex items-center gap-2 font-display text-sm font-semibold tracking-tight">
          <Store className="h-3.5 w-3.5 text-muted-foreground" />
          Marketplace listing
        </h3>
      </header>
      <div className="px-5 py-4">
        {linked ? (
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {listing?.name ?? `#${marketplaceIntegrationId}`}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {listing
                  ? `${listing.slug}${listing.status ? ` · ${listing.status}` : ""}`
                  : listingsQuery.isLoading
                    ? "Resolving listing…"
                    : "Listing details unavailable"}
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={link.isPending}
                >
                  <Unlink className="mr-1.5 h-3.5 w-3.5" />
                  Unlink
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Unlink this listing?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The partner loses access to the portal &ldquo;My
                    Listing&rdquo; editor and any pending change requests
                    can no longer be approved. The public marketplace
                    listing itself is not affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => link.mutate(null)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Unlink
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              No marketplace listing linked. Link one to let this partner
              edit their public listing from the portal.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => setPickerOpen(true)}
            >
              <Link2 className="mr-1.5 h-3.5 w-3.5" />
              Link listing…
            </Button>
          </div>
        )}
      </div>

      <ListingPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        linking={link.isPending}
        onPick={(option) =>
          link.mutate(option.id, {
            onSuccess: () => setPickerOpen(false),
          })
        }
      />
    </section>
  );
}

// integrations-tab LinkPickerDialog shape, filtered client-side — the
// marketplace catalogue is ~8 rows, so no server-side search round-trip.
function ListingPickerDialog({
  open,
  onOpenChange,
  onPick,
  linking,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (option: MarketplaceListingOptionDTO) => void;
  linking: boolean;
}) {
  const [search, setSearch] = useState("");
  const listingsQuery = useMarketplaceListings(open);

  const configured = listingsQuery.data?.configured ?? true;
  const listings = listingsQuery.data?.listings ?? [];
  const term = search.trim().toLowerCase();
  const matches = term
    ? listings.filter(
        (l) =>
          l.name.toLowerCase().includes(term) ||
          l.slug.toLowerCase().includes(term),
      )
    : listings;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setSearch("");
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link a marketplace listing</DialogTitle>
          <DialogDescription>
            Pick the public marketplace listing this partner owns. One
            listing per partner.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or slug…"
            className="pl-9"
            disabled={!configured}
          />
        </div>

        <div className="max-h-72 space-y-1 overflow-y-auto">
          {!configured ? (
            <p className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
              Marketplace connection not configured on the server. Set the
              MARKETPLACE_* secrets to enable linking.
            </p>
          ) : listingsQuery.isLoading ? (
            <div className="space-y-1">
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
            </div>
          ) : listingsQuery.isError ? (
            <p className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
              {listingsQuery.error?.message ?? "Couldn't load listings."}
            </p>
          ) : matches.length === 0 ? (
            <p className="rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
              {search ? "No listings match your search." : "No listings found."}
            </p>
          ) : (
            matches.map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={linking}
                onClick={() => onPick(option)}
                className="flex w-full items-center gap-2 rounded-md border border-transparent px-3 py-2 text-left text-sm hover:border-border/70 hover:bg-muted/40 disabled:opacity-50"
              >
                <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{option.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {option.slug}
                </span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
